import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Dialog,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/CloseRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import { type HistoryItem } from '../hooks/useHistory';
import { useTranslation } from '../i18n';

// ─── Pure helpers (shared across pages) ──────────────────────────────────────

export function featureLabel(endpoint: string, t: ReturnType<typeof useTranslation>): string {
  switch (endpoint) {
    case 'minimax-h3': return t.minimaxH3.navTitle;
    case 'ltx-video': return t.ltxVideo.navTitle;
    case 'wan22-video': return t.wan22Video.navTitle;
    case 'video-add-sound': return t.videoAddSound.navTitle;
    case 'ltx-video-extend': return t.ltxVideoExtend.navTitle;
    case 'image-edit': return t.imageEdit.navTitle;
    case 'multi-angle': return t.multiAngle.navTitle;
    case 'face-swap': return t.faceSwap.navTitle;
    case 'text-to-image': return t.textToImage.navTitle;
    case 'human-portrait': return t.humanPortrait.navTitle;
    case 'instagram-generator': return t.instagramGenerator.navTitle;
    case 'video-face-swap': return t.videoFaceSwap.navTitle;
    default: return endpoint;
  }
}

export function endpointRoute(endpoint: string): string | null {
  switch (endpoint) {
    case 'minimax-h3': return '/minimax-h3';
    // Retired video queues remix onto the H3 page (its remix handler maps
    // the legacy fields).
    case 'ltx-video': return '/minimax-h3';
    case 'wan22-video': return '/minimax-h3';
    // Hidden for now (no page to remix into until they move to H3).
    case 'video-add-sound': return null;
    case 'ltx-video-extend': return null;
    case 'image-edit': return '/image-edit';
    case 'multi-angle': return '/multi-angle';
    case 'face-swap': return '/face-swap';
    case 'text-to-image': return '/text-to-image';
    case 'human-portrait': return '/human-portrait';
    case 'instagram-generator': return '/instagram-generator';
    case 'video-face-swap': return null;
    default: return null;
  }
}

export function isImageEndpoint(endpoint: string): boolean {
  return (
    endpoint === 'image-edit' ||
    endpoint === 'text-to-image' ||
    endpoint === 'multi-angle' ||
    endpoint === 'face-swap' ||
    endpoint === 'human-portrait' ||
    endpoint === 'instagram-generator'
  );
}

export function statusColor(s: HistoryItem['job_status']): 'default' | 'info' | 'success' | 'error' {
  switch (s) {
    case 'completed': return 'success';
    case 'pending':
    case 'processing': return 'info';
    case 'failed': return 'error';
    default: return 'default';
  }
}

// The prompt to show as the primary label for a generation. Prefers the
// enriched prompt (what was actually submitted to the model) and falls back to
// the raw prompt stored in metadata for endpoints without enrichment.
export function promptOf(item: HistoryItem): string | undefined {
  const enriched = item.enriched_prompt;
  if (typeof enriched === 'string' && enriched.trim()) return enriched;
  const p = item.metadata?.input?.prompt;
  if (typeof p === 'string' && p.trim()) return p;
  // Older rows without a stored input still carry the raw prompt column.
  const o = item.original_prompt;
  return typeof o === 'string' && o.trim() ? o : undefined;
}

// The raw prompt the user typed, when enrichment ran and produced a distinct
// enriched prompt. Returns undefined when there's nothing extra to show.
export function originalPromptOf(item: HistoryItem): string | undefined {
  const original = item.original_prompt;
  const enriched = item.enriched_prompt;
  if (
    typeof original === 'string' &&
    original.trim() &&
    typeof enriched === 'string' &&
    enriched.trim() &&
    original.trim() !== enriched.trim()
  ) {
    return original;
  }
  return undefined;
}

export function enrichedPromptOf(item: HistoryItem): string | undefined {
  const enriched = item.enriched_prompt;
  return typeof enriched === 'string' && enriched.trim() ? enriched : undefined;
}

// ─── useResultUrls hook ───────────────────────────────────────────────────────

export type UrlEntry = { urls: string[]; thumbnailUrl?: string };

async function ensureThumbnail(id: number): Promise<void> {
  await fetch(`/api/v1/history/${id}/ensure-thumbnail`, {
    method: 'POST',
    credentials: 'include',
  });
}

export function useResultUrls(
  items: HistoryItem[],
  presignedUrl: (id: number) => Promise<{ url: string; urls: string[]; thumbnailUrl?: string }>
): Record<number, UrlEntry> {
  const [urlsMap, setUrlsMap] = useState<Record<number, UrlEntry>>({});
  const seen = useRef<Set<number>>(new Set());
  const thumbnailPending = useRef<Set<number>>(new Set());

  useEffect(() => {
    items.forEach((it) => {
      if (it.job_status !== 'completed') return;
      if (!it.metadata?.r2_key && !it.metadata?.r2_keys?.length) return;
      if (seen.current.has(it.id)) return;
      seen.current.add(it.id);

      const isVideo =
        it.endpoint === 'minimax-h3' ||
        it.endpoint === 'ltx-video' ||
        it.endpoint === 'wan22-video' ||
        it.endpoint === 'video-add-sound' ||
        it.endpoint === 'ltx-video-extend';
      const needsThumbnail = isVideo && !it.metadata?.thumbnail_r2_key;

      const load = () =>
        presignedUrl(it.id)
          .then(({ urls, thumbnailUrl }) => setUrlsMap((m) => ({ ...m, [it.id]: { urls, thumbnailUrl } })))
          .catch(() => seen.current.delete(it.id));

      if (needsThumbnail && !thumbnailPending.current.has(it.id)) {
        thumbnailPending.current.add(it.id);
        // Trigger background generation, then re-fetch presigned URL after a
        // short delay so the thumbnail URL is included in the response.
        load(); // show media immediately without waiting for thumbnail
        void ensureThumbnail(it.id).then(() =>
          setTimeout(() => {
            presignedUrl(it.id)
              .then(({ urls, thumbnailUrl }) => {
                if (thumbnailUrl) setUrlsMap((m) => ({ ...m, [it.id]: { urls, thumbnailUrl } }));
              })
              .catch(() => {})
          }, 4_000) // give the server ~4s to generate and save
        );
      } else {
        load();
      }
    });
  }, [items, presignedUrl]);
  return urlsMap;
}

// ─── useRemix helper ──────────────────────────────────────────────────────────

// Remix needs a page to land on and something to prefill. Rows without a
// stored `metadata.input` (older writes) still remix from their prompt.
export function canRemix(item: HistoryItem): boolean {
  if (!endpointRoute(item.endpoint)) return false;
  return !!item.metadata?.input || !!item.original_prompt || !!item.enriched_prompt;
}

// Router state for the target page: the stored input plus where it came from,
// so the H3 page can map legacy LTX/WAN settings and re-presign the frames.
export function remixStateFor(item: HistoryItem): { remix: Record<string, unknown>; remixFrom: string; remixActivityId: number } {
  const input = item.metadata?.input;
  const remix: Record<string, unknown> = input && typeof input === 'object'
    ? { ...input }
    : { prompt: item.original_prompt ?? item.enriched_prompt ?? '' };
  return { remix, remixFrom: item.endpoint, remixActivityId: item.id };
}

export function useRemix() {
  const navigate = useNavigate();
  return (item: HistoryItem) => {
    const route = endpointRoute(item.endpoint);
    if (!route) return;
    navigate(route, { state: remixStateFor(item) });
  };
}

// ─── MediaDialog ──────────────────────────────────────────────────────────────

export function MediaDialog({
  item,
  urls,
  onClose,
}: {
  item: HistoryItem | null;
  urls: string[];
  onClose: () => void;
}) {
  const t = useTranslation();
  const [copied, setCopied] = useState(false);
  const isImage = item ? isImageEndpoint(item.endpoint) : false;
  const prompt = item ? promptOf(item) : undefined;
  const originalPrompt = item ? originalPromptOf(item) : undefined;

  const handleCopy = async () => {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <Dialog
      open={!!item}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: 'hidden', maxHeight: '90vh' },
      }}
    >
      {item && (
        <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
          {/* Left: media player */}
          <Box
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              bgcolor: 'black',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
            }}
          >
            {urls.length === 0 ? (
              <CircularProgress size={36} sx={{ color: 'grey.500' }} />
            ) : isImage ? (
              urls.length === 1 ? (
                <Box
                  component="img"
                  src={urls[0]}
                  alt="result"
                  sx={{ width: '100%', maxHeight: '90vh', objectFit: 'contain', display: 'block' }}
                />
              ) : (
                // Multiple images (e.g. multi-angle) — lay them out in a responsive
                // flow grid so all angles are visible at a glance.
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 1,
                    p: 2,
                    width: '100%',
                    alignContent: 'start',
                  }}
                >
                  {urls.map((url, i) => (
                    <Box
                      key={i}
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener"
                      sx={{
                        position: 'relative',
                        aspectRatio: '1 / 1',
                        borderRadius: 1,
                        overflow: 'hidden',
                        display: 'block',
                        bgcolor: 'grey.900',
                      }}
                    >
                      <Box
                        component="img"
                        src={url}
                        alt={`result ${i + 1}`}
                        loading="lazy"
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </Box>
                  ))}
                </Box>
              )
            ) : (
              <Box
                component="video"
                src={urls[0]}
                controls
                autoPlay
                playsInline
                sx={{ width: '100%', maxHeight: '90vh', display: 'block' }}
              />
            )}
          </Box>

          {/* Right: details panel */}
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                {featureLabel(item.endpoint, t)}
              </Typography>
              <IconButton size="small" onClick={onClose} sx={{ mt: -0.5, mr: -0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Scrollable details */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {t.history.detail.jobId}
                  </Typography>
                  {item.external_request_id ? (
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, wordBreak: 'break-all' }}
                    >
                      {item.external_request_id}
                    </Typography>
                  ) : (
                    <Typography variant="body2" fontWeight={500}>#{item.id}</Typography>
                  )}
                </Box>

                {item.batch_job_id && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      {t.history.detail.batchJobId}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, wordBreak: 'break-all' }}
                    >
                      {item.batch_job_id}
                      {item.batch_queue ? ` (${item.batch_queue})` : ''}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    Status
                  </Typography>
                  <Chip
                    size="small"
                    label={t.history.status[item.job_status]}
                    color={statusColor(item.job_status)}
                    sx={{ mt: 0.25 }}
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    Model
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>
                    {item.model_name}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {new Date(item.created_at).toLocaleString()}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    Credits
                  </Typography>
                  <Typography variant="body2">
                    {Number(item.credits_used).toLocaleString()} {t.credits.balanceUnit}
                  </Typography>
                </Box>

                {prompt && (
                  <>
                    <Divider />
                    {/* When enrichment ran, show the user's original prompt as an
                        extra row above the enhanced prompt that was submitted. */}
                    {originalPrompt && (
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                          Original prompt
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}
                        >
                          {originalPrompt}
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {originalPrompt ? 'Enhanced prompt' : 'Prompt'}
                        </Typography>
                        <Tooltip title={copied ? t.history.detail.copied : t.history.detail.copyPrompt} arrow>
                          <IconButton size="small" onClick={handleCopy}>
                            {copied
                              ? <CheckIcon sx={{ fontSize: 13, color: 'success.main' }} />
                              : <ContentCopyIcon sx={{ fontSize: 13 }} />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}
                      >
                        {prompt}
                      </Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </Box>

            {/* Footer: download button(s) */}
            {urls.length > 0 && (
              <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <Stack spacing={0.75}>
                  {urls.map((url, i) => (
                    <Button
                      key={i}
                      size="small"
                      fullWidth
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      href={url}
                      download
                      target="_blank"
                      rel="noopener"
                    >
                      {urls.length > 1 ? `Download ${i + 1}` : t.history.detail.download}
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
}
